import { Link } from 'react-router-dom';
import { ASSETS, RETREAT, FUTURE_RETREATS } from '../lib/content';
import { Trees, MapPin, CalendarDays, Users, Compass } from 'lucide-react';
import Newsletter from '../components/Newsletter';

export default function Retreats() {
  return (
    <div data-testid="page-retreats">
      <section className="relative h-[60vh] min-h-[460px]">
        <img src={ASSETS.goldField} alt="Golden field under warm light" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-brand-bg/30 via-brand-bg/10 to-brand-bg" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl mx-auto px-6 md:px-12 pb-16 w-full">
            <p className="font-sans text-[11px] uppercase tracking-[0.28em] text-brand-brown-light mb-4 bg-brand-bone/80 inline-block px-4 py-1.5 rounded-full">In-person gatherings</p>
            <h1 className="font-serif text-5xl md:text-6xl text-brand-brown leading-[1.05] max-w-3xl">
              Workshops <em className="italic text-brand-brown-light">&</em> Retreats
            </h1>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <p className="font-sans text-base md:text-lg text-brand-brown-light/90 leading-relaxed">
            Alongside the online Paths, we gather in person from time to time — in small, intentionally held groups — for retreats and workshops that allow the work to land more deeply, in body and in community.
          </p>
        </div>
      </section>

      {/* FEATURED RETREAT */}
      <section className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="bg-brand-bone rounded-3xl overflow-hidden border border-brand-brown/5">
            <div className="grid md:grid-cols-2">
              <div className="relative aspect-[4/3] md:aspect-auto">
                <img src={ASSETS.sunThroughTrees} alt="Sunlight through trees in a quiet forest" className="absolute inset-0 w-full h-full object-cover" />
              </div>
              <div className="p-8 md:p-12">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-sage text-brand-brown text-[11px] uppercase tracking-[0.22em] mb-5">
                  <Trees size={12} /> Featured Retreat
                </div>
                <h2 className="font-serif text-3xl md:text-4xl text-brand-brown leading-tight">{RETREAT.title}</h2>
                <p className="mt-4 font-sans text-base text-brand-brown-light/90 leading-relaxed">{RETREAT.description}</p>

                <dl className="mt-7 space-y-4">
                  <div className="flex items-start gap-3">
                    <CalendarDays size={18} className="text-brand-gold mt-0.5" />
                    <div>
                      <dt className="font-sans text-[11px] uppercase tracking-[0.22em] text-brand-brown-muted">When</dt>
                      <dd className="font-serif text-lg text-brand-brown">{RETREAT.dates} · {RETREAT.duration}</dd>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin size={18} className="text-brand-gold mt-0.5" />
                    <div>
                      <dt className="font-sans text-[11px] uppercase tracking-[0.22em] text-brand-brown-muted">Where</dt>
                      <dd className="font-serif text-lg text-brand-brown">{RETREAT.venue}</dd>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Users size={18} className="text-brand-gold mt-0.5" />
                    <div>
                      <dt className="font-sans text-[11px] uppercase tracking-[0.22em] text-brand-brown-muted">Who it’s for</dt>
                      <dd className="font-sans text-sm text-brand-brown-light/90 leading-relaxed">{RETREAT.audience}</dd>
                    </div>
                  </div>
                </dl>

                <Link
                  to="/booking"
                  data-testid="retreat-cta-enquire"
                  className="mt-8 inline-flex items-center font-sans text-sm tracking-wide bg-brand-brown text-brand-bg rounded-full px-7 py-3 hover:bg-brand-brown-light transition-colors duration-500"
                >
                  Enquire about this retreat
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <h3 className="font-serif text-3xl md:text-4xl text-brand-brown text-center mb-12">What the retreat holds</h3>
          <div className="grid sm:grid-cols-2 gap-5">
            {RETREAT.includes.map((i, idx) => (
              <div key={idx} data-testid={`retreat-include-${idx}`} className="bg-brand-bg-soft rounded-2xl p-6 border border-brand-brown/5 flex items-start gap-4">
                <div className="h-2 w-2 rounded-full bg-brand-gold mt-2.5" />
                <p className="font-sans text-base text-brand-brown-light/90 leading-relaxed">{i}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-brand-sage">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <p className="font-serif italic text-2xl md:text-3xl text-brand-brown-light leading-relaxed">
            A few quiet days, away from the noise — to remember the rhythm of our own breath.
          </p>
          <Link
            to="/booking"
            data-testid="retreat-cta-bottom"
            className="mt-10 inline-flex font-sans text-sm tracking-wide bg-brand-brown text-brand-bg rounded-full px-8 py-3.5 hover:bg-brand-brown-light transition-colors duration-500"
          >
            Reach out about retreats
          </Link>
        </div>
      </section>

      {/* FUTURE 7-DAY RETREATS */}
      <section className="py-24 md:py-28">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <Compass size={20} className="text-brand-gold mx-auto mb-4" />
            <p className="font-sans text-[11px] uppercase tracking-[0.28em] text-brand-brown-muted mb-4">Looking ahead</p>
            <h2 className="font-serif text-4xl md:text-5xl text-brand-brown leading-tight">Seven-day in-person retreats</h2>
            <p className="mt-5 font-sans text-base text-brand-brown-light max-w-2xl mx-auto leading-relaxed">
              A future thread of the work — week-long, residential retreats woven around the three foundational themes of Nurture the Self. Held in the company of careful facilitation, nourishing food, daily rhythm, embodiment practices and quiet time in nature.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {FUTURE_RETREATS.map((r, i) => (
              <div key={i} data-testid={`future-retreat-${i}`} className="bg-brand-bg-soft rounded-3xl p-8 border border-brand-brown/5">
                <p className="font-sans text-[11px] uppercase tracking-[0.28em] text-brand-gold mb-3">Theme {i + 1}</p>
                <h3 className="font-serif text-2xl text-brand-brown leading-snug mb-3">{r.theme}</h3>
                <p className="font-sans text-sm text-brand-brown-light/90 leading-relaxed">{r.body}</p>
              </div>
            ))}
          </div>

          <p className="mt-10 text-center font-sans text-sm text-brand-brown-muted italic max-w-3xl mx-auto">
            Dates and venues will be announced gently, in time.
          </p>
        </div>
      </section>

      {/* FOREST IMAGE BREAK + NEWSLETTER */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="relative h-[320px] md:h-[400px] rounded-3xl overflow-hidden">
            <img src={ASSETS.heroMeadow} alt="Soft lavender meadow at warm light" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-brand-bg/40 to-transparent" />
            <div className="absolute inset-0 flex items-center">
              <div className="px-8 md:px-14 max-w-md">
                <p className="font-serif italic text-2xl md:text-3xl text-brand-bone leading-snug" style={{ textShadow: '0 1px 12px rgba(44,34,30,0.35)' }}>
                  “Held in the rhythm of nature, the body remembers how to rest.”
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <Newsletter source="retreats" tone="sage" />
        </div>
      </section>
    </div>
  );
}
